import clsx from 'clsx';
import PropTypes from 'prop-types';
import { forwardRef, memo, useId, useRef, useEffect } from 'react';
// eslint-disable-next-line import/no-unresolved
import { register } from 'swiper/element';
// eslint-disable-next-line import/no-unresolved
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

import { useIsMounted } from '../../hooks/use-is-mounted';

import { CarouselArrow } from './carousel-arrow/carousel-arrow';
import { CarouselSlide } from './carousel-slide/carousel-slide';

import './carousel.scss';

const PAGINATION_INLINE_STYLES = `
  .swiper-pagination {
    display: flex;
    gap: 0.25rem;
    justify-content: center;
    margin-top: 0.5rem;
    padding-left: 0;
    position: relative;
  }
  .swiper-pagination-lock {
    display: none;
  }
  .swiper-pagination-bullet {
    background: var(--color-hint);
    border: 0;
    border-radius: 0.25rem;
    display: flex;
    height: 0.5rem;
    opacity: 1;
    padding: 0;
    transition: width 0.3s ease-in-out;
    width: 0.5rem;
    will-change: width;
  }
  .swiper-pagination-bullet-active {
    background: var(--primary-base);
    width: 1.25rem;
  }
`;

const CarouselUI = forwardRef(
  (
    { children, className, hasNavigation, showPlaceholder, updateDependencies, placeholder, iconArrowName, ...rest },
    forwardedRef
  ) => {
    const carouselId = useId();
    const fallbackRef = useRef();
    const ref = forwardedRef || fallbackRef;
    const isMounted = useIsMounted();

    useEffect(() => {
      register();
    }, []);

    useEffect(() => {
      if (!isMounted || showPlaceholder) return;
      const swiperContainer = ref.current;
      const params = {
        modules: [Autoplay, Navigation, Pagination],
        injectStyles: [rest.pagination && PAGINATION_INLINE_STYLES],
        navigation: hasNavigation
          ? {
              nextEl: `[data-carousel-arrow-id="next-${carouselId}"]`,
              prevEl: `[data-carousel-arrow-id="prev-${carouselId}"]`,
            }
          : undefined,
        ...rest,
      };

      Object.assign(swiperContainer, params);
      swiperContainer.initialize();
    }, [isMounted, showPlaceholder]);

    useEffect(() => {
      ref.current?.swiper?.update();
    }, updateDependencies);

    if (!isMounted || showPlaceholder) return placeholder;

    return (
      <div className={clsx(className, 'sb-Carousel', !isMounted && 'isNotMounted')}>
        <swiper-container ref={ref} init="false">
          {children}
        </swiper-container>

        {hasNavigation && isMounted && (
          <div className={clsx('sb-CarouselArrows', rest.pagination && 'withPagination')}>
            <CarouselArrow action="prev" data-carousel-arrow-id={`prev-${carouselId}`} iconArrowName={iconArrowName} />
            <CarouselArrow action="next" data-carousel-arrow-id={`next-${carouselId}`} iconArrowName={iconArrowName} />
          </div>
        )}
      </div>
    );
  }
);

CarouselUI.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  className: PropTypes.string,
  hasNavigation: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  updateDependencies: PropTypes.arrayOf(PropTypes.any),
  placeholder: PropTypes.element,
  showPlaceholder: PropTypes.bool,
  iconArrowName: PropTypes.string,
};

CarouselUI.defaultProps = {
  className: undefined,
  hasNavigation: true,
  updateDependencies: [],
  placeholder: null,
  showPlaceholder: false,
  iconArrowName: undefined,
};

export const Carousel = memo(CarouselUI);

export { CarouselArrow, CarouselSlide };
